<template>
	<div class="drawing-tools">
		<v-tooltip top>
			<template #activator="{ on: tooltip }">
				<v-btn v-on="tooltip" flat icon small class="activator-button" @click="drawingTools.toggleDrawingToolActive()">
					<v-icon :style="drawingToolsButtonStyle">{{ drawingToolIcon }}</v-icon>
				</v-btn>
			</template>
			<span>Drawing tools</span>
		</v-tooltip>
		<v-menu
			style="display: inline-block"
			top
			offset-y
			:close-on-content-click="false"
			min-width="280"
			max-width="320"
			z-index="1000"
		>
			<template #activator="{ on: menu }">
				<v-btn v-on="menu" flat icon small class="panel-button">
					<v-icon :style="drawingToolsButtonStyle">mdi-chevron-down</v-icon>
				</v-btn>
			</template>
			<div class="drawing-tools-panel">
				<div v-if="isPencilEnabled" class="pencil-options">
					<div class="color-options">
						<p>Pencil brush color</p>
						<div class="colors">
							<div
								v-for="color in pencilColors"
								:key="color.id"
								class="color"
								:class="{ selected: drawingTools.pencilBrushColor === color.id }"
								:style="{ backgroundColor: color.value }"
								@click="drawingTools.selectPencilBrushColor(color.id)"
							></div>
						</div>
					</div>
					<div class="width-options">
						<p>Pencil brush width</p>
						<div class="width-slider">
							<v-slider
								:value="drawingTools.pencilBrushWidth"
								min="1"
								max="30"
								hide-details
								@input="drawingTools.updatePencilBrushWidth"
							></v-slider>
							<p>{{ drawingTools.pencilBrushWidth }}</p>
						</div>
					</div>
				</div>
				<div v-else-if="isShapeEnabled" class="shape-options">
					<div class="selection-options">
						<p>Select shape</p>
						<div class="shapes">
							<div
								v-for="shape in shapes"
								:key="shape.id"
								class="shape"
								:class="{ selected: drawingTools.selectedShape === shape.id }"
								@click="drawingTools.selectShape(shape.id)"
							>
								<v-icon>{{ 'mdi-' + shape.icon }}</v-icon>
							</div>
						</div>
					</div>
					<div v-if="!isLineShape" class="color-options">
						<p>Shape fill color</p>
						<div class="colors">
							<div
								v-for="color in shapeColors"
								:key="color.id"
								class="color"
								:class="{ selected: drawingTools.shapeFillColor === color.id }"
								:style="{ backgroundColor: color.value }"
								@click="drawingTools.selectShapeFillColor(color.id)"
							>
								<v-icon v-if="isTransparentColor(color.id)" color="red">mdi-cancel</v-icon>
							</div>
						</div>
					</div>
					<div class="color-options">
						<p>Shape stroke color</p>
						<div class="colors">
							<div
								v-for="color in shapeColors"
								:key="color.id"
								class="color"
								:class="{ selected: drawingTools.shapeStrokeColor === color.id }"
								:style="{ backgroundColor: color.value }"
								@click="drawingTools.selectShapeStrokeColor(color.id)"
							>
								<v-icon v-if="isTransparentColor(color.id)" color="red">mdi-cancel</v-icon>
							</div>
						</div>
					</div>
					<div class="width-options">
						<p>Shape stroke width</p>
						<div class="width-slider">
							<v-slider
								:value="drawingTools.shapeStrokeWidth"
								min="1"
								max="30"
								hide-details
								@input="drawingTools.updateShapeStrokeWidth"
							></v-slider>
							<p>{{ drawingTools.shapeStrokeWidth }}</p>
						</div>
					</div>
				</div>
				<div v-else-if="isEraserEnabled" class="eraser-options">
					<p>Erase lines or shapes</p>
				</div>
				<div v-else class="no-options">
					<p>Select a drawing tool</p>
				</div>
				<div class="drawing-tools-options">
					<v-tooltip v-for="option in drawingToolOptions" :key="option.id" top>
						<template #activator="{ on: tooltip }">
							<div
								class="option"
								:class="{
									selected: drawingTools.isDrawingToolActive && drawingTools.selectedDrawingTool === option.id
								}"
								v-on="{ ...tooltip }"
								@click="drawingTools.selectDrawingTool(option.id)"
							>
								<v-icon>{{ 'mdi-' + option.icon }}</v-icon>
							</div>
						</template>
						<span>{{ option.title }}</span>
					</v-tooltip>
				</div>
			</div>
		</v-menu>
	</div>
</template>

<script>
import { DRAWING_TOOLS, COLORS, SHAPES } from '../../../lessonPlan/models/drawingTools';

export default {
	name: 'DrawingTools',
	inject: ['drawingTools'],
	data() {
		return {
			drawingToolOptions: [
				{ id: DRAWING_TOOLS.PENCIL, title: 'Pencil', icon: 'draw' },
				{ id: DRAWING_TOOLS.SHAPE, title: 'Shape', icon: 'shape-outline' },
				{ id: DRAWING_TOOLS.ERASER, title: 'Eraser', icon: 'eraser' },
				{ id: DRAWING_TOOLS.CLEAR, title: 'Clear', icon: 'broom' }
			],
			colors: [
				{ id: COLORS.TRANSPARENT, value: 'transparent' },
				{ id: COLORS.WHITE, value: 'white' },
				{ id: COLORS.BLACK, value: 'black' },
				{ id: COLORS.INDIAN_RED, value: 'indianred' },
				{ id: COLORS.RED, value: 'red' },
				{ id: COLORS.ORANGE, value: 'orange' },
				{ id: COLORS.YELLOW, value: 'yellow' },
				{ id: COLORS.LAWN_GREEN, value: 'lawngreen' },
				{ id: COLORS.GREEN, value: 'green' },
				{ id: COLORS.CYAN, value: 'cyan' },
				{ id: COLORS.BLUE, value: 'blue' },
				{ id: COLORS.DARK_BLUE, value: 'darkblue' },
				{ id: COLORS.PURPLE, value: 'purple' }
			],
			shapes: [
				{ id: SHAPES.RECTANGLE, icon: 'rectangle-outline' },
				{ id: SHAPES.ELLIPSE, icon: 'ellipse-outline' },
				{ id: SHAPES.LINE, icon: 'slash-forward' }
			]
		};
	},
	computed: {
		pencilColors() {
			return this.colors.filter(color => color.id !== COLORS.TRANSPARENT);
		},
		shapeColors() {
			return this.colors;
		},
		drawingToolIcon() {
			const drawingTool = this.drawingToolOptions.find(option => option.id === this.drawingTools.selectedDrawingTool);

			if (drawingTool) return 'mdi-' + drawingTool.icon;

			const pencilTool = this.drawingToolOptions.find(option => option.id === DRAWING_TOOLS.PENCIL);

			return 'mdi-' + pencilTool.icon;
		},
		drawingToolsButtonStyle() {
			if (!this.drawingTools.isDrawingToolActive) return { borderBottom: `3px solid ${COLORS.TRANSPARENT}` };

			const borderColor = {
				[DRAWING_TOOLS.PENCIL]: this.drawingTools.pencilBrushColor,
				[DRAWING_TOOLS.SHAPE]: this.getShapeColor(),
				[DRAWING_TOOLS.ERASER]: COLORS.BLACK
			};

			return { borderBottom: `3px solid ${borderColor[this.drawingTools.selectedDrawingTool]}` };
		},
		isPencilEnabled() {
			return this.drawingTools.isPencilEnabled;
		},
		isShapeEnabled() {
			return this.drawingTools.isShapeEnabled;
		},
		isEraserEnabled() {
			return this.drawingTools.isEraserEnabled;
		},
		isLineShape() {
			return this.drawingTools.selectedShape === SHAPES.LINE;
		}
	},
	methods: {
		isTransparentColor(selectedColor) {
			return selectedColor === COLORS.TRANSPARENT;
		},
		getShapeColor() {
			let shapeColor = this.drawingTools.shapeStrokeColor;
			if (shapeColor === COLORS.TRANSPARENT || shapeColor === COLORS.WHITE) shapeColor = COLORS.BLACK;

			return shapeColor;
		}
	}
};
</script>

<style scoped>
.drawing-tools {
	display: flex;
	align-items: center;
}

.drawing-tools .activator-button {
	margin-right: 0;
}

.drawing-tools .panel-button {
	margin-left: 0;
}

.drawing-tools .activator-button,
.drawing-tools .panel-button {
	width: 24px;
	height: 24px;
}

.drawing-tools-panel {
	display: flex;
	flex-direction: column;
}

.drawing-tools-options {
	display: flex;
	border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.drawing-tools-options .option {
	display: flex;
	flex: 1;
	align-items: center;
	justify-content: center;
	height: 40px;
	cursor: pointer;
}

.drawing-tools-options .option:hover,
.drawing-tools-options .option.selected {
	background-color: rgba(0, 0, 0, 0.12);
}

.pencil-options,
.shape-options,
.eraser-options,
.no-options {
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	height: 240px;
}

.eraser-options,
.no-options {
	align-items: center;
	justify-content: center;
}

.eraser-options p,
.no-options p {
	font-size: 16px;
	font-weight: 500;
}

.selection-options {
	padding: 8px 8px 0 8px;
	display: flex;
	flex-direction: column;
}

.selection-options p {
	font-weight: 500;
	margin-bottom: 0px;
}

.selection-options .shapes {
	gap: 8px;
	flex: 1 1 auto;
	display: flex;
}

.selection-options .shapes .shape {
	padding: 2px;
	cursor: pointer;
}

.selection-options .shapes .shape.selected {
	background-color: rgba(0, 0, 0, 0.12);
	border-radius: 4px;
}

.color-options {
	padding: 8px;
	display: flex;
	flex-direction: column;
}

.color-options p {
	font-weight: 500;
	margin-bottom: 4px;
}

.color-options .colors {
	gap: 4px;
	flex: 1 1 auto;
}

.pencil-options .color-options .colors {
	display: grid;
	grid-template-columns: repeat(6, auto);
	grid-auto-rows: auto;
}

.shape-options .color-options .colors {
	display: flex;
}

.color-options .colors .color {
	flex: 1;
	border: 1px solid rgba(0, 0, 0, 0.12);
	cursor: pointer;
	aspect-ratio: 1 / 1;
}

.color-options .colors .color:has(i) {
	border: none !important;
	display: flex;
	justify-content: center;
	align-items: center;
}

.color-options .colors .color i {
	font-size: 16px;
}

.color-options .colors .color.selected {
	border: 1px solid white;
	outline: 2px solid rgba(0, 0, 0, 0.72);
}

.width-options {
	padding: 8px;
	display: flex;
	flex-direction: column;
}

.width-options p {
	font-weight: 500;
	margin-bottom: 0px;
}

.width-options .width-slider {
	display: flex;
	align-items: center;
	gap: 16px;
}

.width-options .width-slider > * {
	margin: 0;
}
</style>
